import React, { useCallback, useRef, useState } from 'react';
import { OptionEntry, OptionKey } from './option';
import { ScreenCycleTableRowProps } from './ScreenCycleTableRow';
import { STATE_COLORS } from '../CycleTable/constants';
import ActionButtons from './ActionButtons';
import CycleActions from '../CycleActions/CycleActions';
import { IoTDataResponse } from '../../store/models/cycles';
import Modal from '../Modal/Modal';
import { ChannelState } from '../../enums/channel-state.enum';
import ListFaultedChannels from '../ListFaultedChannels';
import InteractiveButton from '../InteractiveButton/InteractiveButton';
import { ChannelActionItem } from '../../store/models/channels';
import getChannelNumByString from '../../utils/getChannelNumByString';
import { ChangeChannelAction } from '../../enums/action';
import { connect } from 'react-redux';
import { Dispatch } from '../../store/store';
import { CycleController } from '../../store/models/controllers';
import { IControllerSettings } from '../../types/controller-types';
import { IValueWithTime } from '../../types/channel-types';
import { FaultedChannelData } from '../ListFaultedChannels/types/FaultedChannelData';
import CycleTablePreviewModal from '../CycleTablePreviewModal/CycleTablePreviewModal';

type ComponentProps = Omit<ScreenCycleTableRowProps, 'cycleOption'> & {
  optionKey: OptionKey,
  option: OptionEntry,
} & ReturnType<typeof mapDispatch>;

const CycleCell: React.FC<ComponentProps> = ({
  optionKey,
  option,
  cycle,
  actionsButtons,
  setSelectedCycle,
  setIsPackageModalOpen,
  ability,
  updateChannels,
}) => {
  const isFaultedHighestState = ChannelState.FAULTED === Number(cycle.highest_state);

  const [listIotDataChannels, setListIotDataChannels] = useState<FaultedChannelData[] | null>(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const nameRef = useRef<HTMLParagraphElement | null>(null);

  const getConnectedControllers = (controllers: CycleController[]) => {
    return controllers.reduce((accumulator, cycleControllers) => {
      const controller = cycleControllers.controller
      if (!controller.connected) {
        return accumulator;
      }

      accumulator.add(controller.serial_number);
      return accumulator;
    }, new Set<string>());
  };

  const getFaultedControllers = (data: Record<string, IControllerSettings>) => {
    const result: Record<string, IValueWithTime<string[]>> = {};

    for (const [deviceId, deviceData] of Object.entries(data)) {
      const faults = deviceData.faults?.value;
      if (Array.isArray(faults) && faults.length > 0) {
        result[deviceId] = deviceData.faults;
      }
    }

    return result;
  };

  const getControllerName = (controllers: CycleController[]) => {
    return controllers.reduce((accumulator, cycleControllers) => {
      const name = cycleControllers.controller.name;
      accumulator[cycleControllers.controller.serial_number] = name;
      return accumulator;
    }, {});
  }

  const openModal = useCallback(() => {
    if (!isFaultedHighestState) return;

    const connectedControllers = getConnectedControllers(cycle.data.cycle_controllers);
    const faultedControllers = getFaultedControllers(cycle.data.controllersSettings);
    const controllerName = getControllerName(cycle.data.cycle_controllers);
    const channelNames = cycle.data?.deviceNames;

    const faultedChannels = cycle.data.iotDataResponse.reduce<FaultedChannelData[]>(
      (accumulator, item) => {
        if (!item?.channel) {
          return accumulator;
        };

        if (!connectedControllers.has(item.serial)) {
          accumulator.push({
            channelName: channelNames[item.pk] || getChannelNumByString(item.channel),
            controllerName: controllerName[item.serial] || item.serial,
            id: item.pk,
            isControllerConnected: false,
            timestamp: item.sk,
          });
          return accumulator;
        };

        if (
          faultedControllers[item.serial] ||
        item.state === ChannelState.FAULTED ||
        (item.manualFault !== null && item.manualFault !== undefined)
        ) {
          const channelFaultMessage = item.manualFault?.message ? [item.manualFault.message] : undefined;
          accumulator.push({
            channelName: channelNames[item.pk] || getChannelNumByString(item.channel),
            controllerName: controllerName[item.serial] || item.serial,
            id: item.pk,
            isControllerConnected: true,
            timestamp: item.manualFault?.faultDatetime || item.sk,
            controllerFaultMessages: faultedControllers[item.serial]?.value,
            channelFaultMessage,
          });
          return accumulator;
        }

        return accumulator;
      }, []
    );

    setListIotDataChannels(faultedChannels);
  }, [cycle.data.iotDataResponse, isFaultedHighestState]);

  const resetIotData = () => {
    setListIotDataChannels(null);
  }

  const renderCellContent = () => {
    switch (optionKey) {
    case OptionKey.ACTIONS: {
      return (
        <ActionButtons
          cycle={cycle}
          actionsButtons={actionsButtons}
          setSelectedCycle={setSelectedCycle}
          setIsPackageModalOpen={setIsPackageModalOpen}
          ability={ability}
        />
      );
    }

    case OptionKey.CYCLE_DEVICE_ACTIONS: {
      if (Array.isArray(cycle[option.KEY])) {
        return (
          <div className='flex justify-center items-center'>
            <CycleActions className='auto-cols-[30px]' actions={cycle[option.KEY]} />
          </div>
        );
      }

      return <p>{cycle[option.KEY]}</p>;
    }

    case OptionKey.HIGHEST_STATE: {
      return (
        <span
          style={{
            color: STATE_COLORS[cycle[option.KEY]],
            cursor: isFaultedHighestState ? 'pointer' : 'auto',
          }}
          className="relative bottom-[2px] text-[8px] leading-[8px] md:text-xs/3"
          onClick={openModal}
        >
            ⬤
        </span>
      )
    }

    case OptionKey.NAME: {
      return (
        <p
          ref={nameRef}
          className={`${option.BLOCK_PARAMETERS} cursor-pointer text-primary hover:underline`}
          onClick={() => setIsPreviewOpen(true)}
        >
          {cycle[option.KEY]}
        </p>
      );
    }

    default: {
      return <p className={option.BLOCK_PARAMETERS}>{cycle[option.KEY]}</p>;
    }
    }
  };

  const resetFaultsForAllChannels = async (cycleId: string, listIotDataChannels: IoTDataResponse[]) => {
    const faultedControllers = getFaultedControllers(cycle.data.controllersSettings);

    const list: ChannelActionItem[] = listIotDataChannels.map((item) => {
      const hasActualIoTFault = item.state === ChannelState.FAULTED || faultedControllers[item.serial];
      const hasManualFault = item.manualFault !== null && item.manualFault !== undefined;
      
      const manualFaultsOnly = hasManualFault && !hasActualIoTFault;
      
      return { 
        controller_serial_number: item.serial, 
        channel_num: String(getChannelNumByString(item.channel)),
        ...(manualFaultsOnly && { manualFaultsOnly: true })
      };
    });

    await updateChannels({
      cycleId,
      data: { action: ChangeChannelAction.RESET_CHANNEL_FAULTS, list },
    });
  };

  const nameDateStyles = optionKey === 'NAME' || optionKey === 'COMPLETED_DATE' ? 'pl-4 text-left' : '';

  return (
    <>
      <td className={`c-cycle-table__td py-1 ${nameDateStyles}`}>
        {renderCellContent()}
      </td>
      <Modal
        modalTitle='Faulted Channels'
        isVisible={Boolean(listIotDataChannels?.length)}
        onClose={resetIotData}
        showAcceptBtn={false}
        showCancelBtn={false}
      >
        <div className='flex flex-col justify-between h-full'>
          {listIotDataChannels && (
            <ListFaultedChannels
              className='mb-3'
              listIotDataChannels={listIotDataChannels}
            />
          )}
          <InteractiveButton
            text="Reset faulted channels"
            className='w-full'
            onClick={() => resetFaultsForAllChannels(cycle.id, cycle.data.iotDataResponse)}
          />
        </div>
      </Modal>
      <CycleTablePreviewModal
        cycleId={cycle.id}
        cycleName={cycle[option.KEY]}
        isVisible={isPreviewOpen}
        onClose={() => setIsPreviewOpen(false)}
        anchorRef={nameRef}
      />
    </>
  );
};

const mapDispatch = (dispatch: Dispatch) => ({
  updateChannels: dispatch.channels.updateChannels,
});

export default connect(null, mapDispatch)(CycleCell);
